import { useSDMCartContext, useSDMCustomerContext, type ISDMContextsProps } from '@ldp/sdm-contexts';
import { useRouter } from 'next/router';
import { FC } from 'react';

// @ts-ignore: federated module
import ViewResolverSDM from 'ldpViewResolverSdm/Resolver';
// @ts-ignore: federated module
import ViewResolverSection from 'ldpViewResolverSdm/Section';
// @ts-ignore: federated module
import MastheadComponent from 'ldpViewResolverSdm/MastheadComponent';

import { COOKIE_VALUE, VIEW_RESOLVER } from '@/data/constants';
import { useCommonContextProps, useContentfulPreview } from '@/hooks';
import { useTrackPageView } from '@/hooks/analytics';
import { getOriginSessionNextCookie } from '@/utils';
import { IViewDefinition } from '@/utils/view-definition.interface';
import { Spinner } from 'Components/shared';

/** Don't forget to add prop types */
export interface IViewResolverComponentProps {
  /** The page id for which data needs to be resolved */
  pageId: string;
  /** The view definition of the SDUI page */
  initialData?: IViewDefinition;
  /** The theme token for the SDUI page */
  themeTokens?: object;
  /** The seo object of the SDUI page */
  seo: any;
  headerSection: object | null | undefined;
}

/** This describes how the federated module can be used in legacy apps */
export const ViewResolverComponent: FC<IViewResolverComponentProps> = ({
  initialData,
  themeTokens,
  headerSection,
  ...rest
}) => {
  const { renderDatePicker, isContentfulPreview, selectedDateTime } = useContentfulPreview();
  const router = useRouter();

  const { commonContextProps, locale } = useCommonContextProps();

  const { API_VERSION_V2: apiVersion, MAIN_CONTENT_COLLECTION } = VIEW_RESOLVER;

  const originSessionCookie = getOriginSessionNextCookie();
  const isOriginSessionM4 = originSessionCookie === COOKIE_VALUE.M4;

  const sdmContextProps: ISDMContextsProps = {
    sdmCartProviderProps: {
      ...useSDMCartContext(),
    },
    sdmCustomerProviderProps: {
      ...useSDMCustomerContext(),
    },
    sdmRouterProps: { ...router },
  };

  //analytics
  const { cartCount, isInitialCartCountUpdated, setPageId } = useSDMCartContext();
  const { pcOptimumAccount } = useSDMCustomerContext();
  const { pageId } = rest;

  setPageId(pageId);
  useTrackPageView(cartCount, pageId, pcOptimumAccount!, isInitialCartCountUpdated);

  return (
    <>
      <ViewResolverSDM
        {...rest}
        hadExistingSentry={true}
        isPreview={isContentfulPreview}
        previewDatetime={selectedDateTime}
        commonContexts={commonContextProps}
        sdmContexts={sdmContextProps}
        queryConfig={{
          // The initial data will be used to populate the cache, could be SSR fetched
          initialData,
          // Setting staleTime to Infinity will prevent the data from being refetched
          staleTime: Infinity,
        }}
        themeTokens={themeTokens}
        PageLoadingFallback={() => <Spinner />}
        locale={locale}
        env={process.env.NEXT_PUBLIC_VIEW_RESOLVER_ENV}
      >
        {/*  only render MastheadComponent where header section is not null until M4 launches */}
        {headerSection && isOriginSessionM4 ? <MastheadComponent /> : null}
        <ViewResolverSection apiVersion={apiVersion} layoutSection={MAIN_CONTENT_COLLECTION} />
        {/* SDUI footer */}
        {isOriginSessionM4 ? (
          <ViewResolverSection apiVersion={apiVersion} layoutSection={MAIN_CONTENT_COLLECTION} hasFooter />
        ) : null}
      </ViewResolverSDM>
      {isContentfulPreview ? renderDatePicker() : null}
    </>
  );
};

export default ViewResolverComponent;
